import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useFloating, offset, flip, shift, limitShift, autoUpdate, useClick, useHover, safePolygon, useDismiss, useInteractions, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';

function Popover({ trigger, anchorElement, triggerType = "click", open, onClose, children, placement, offset: offset$1, shift: shift$1, root, inline, disableScroll, disableFlip, }) {
    const [internalOpen, setInternalOpen] = useState(false);
    const _open = open ?? internalOpen;
    const floating = useFloating({
        open: _open,
        onOpenChange: (open) => {
            setInternalOpen(open);
            if (!open) {
                onClose?.();
            }
        },
        placement,
        transform: true,
        elements: anchorElement
            ? {
                reference: anchorElement,
            }
            : undefined,
        middleware: [
            offset(offset$1),
            !disableFlip && flip(),
            !!shift$1 &&
                shift({
                    crossAxis: shift$1.crossAxis,
                    mainAxis: shift$1.mainAxis,
                    padding: shift$1.padding,
                    limiter: limitShift(),
                }),
        ],
        // This makes sure we re-position the floating element when it resizes
        whileElementsMounted(referenceEl, floatingEl, update) {
            const cleanup = autoUpdate(referenceEl, floatingEl, update, {
                elementResize: true,
            });
            return cleanup;
        },
    });
    const click = useClick(floating.context, {
        enabled: triggerType === "click",
    });
    const hover = useHover(floating.context, {
        enabled: triggerType === "hover",
        move: false,
        mouseOnly: true,
        delay: { open: 100 },
        handleClose: safePolygon(),
    });
    const dismiss = useDismiss(floating.context, {
        enabled: triggerType === "click",
        outsidePress(event) {
            if (anchorElement?.contains(event.target)) {
                return false;
            }
            return true;
        },
    });
    const interactions = useInteractions([hover, click, dismiss]);
    function closeMenu() {
        setInternalOpen(false);
    }
    useEffect(() => {
        if (_open && disableScroll) {
            document.body.style.overflowY = "hidden";
        }
        else {
            document.body.style.overflowY = "";
        }
        return () => {
            document.body.style.overflowY = "";
        };
    }, [_open, disableScroll]);
    return (jsxs(Fragment, { children: [trigger && !anchorElement && (jsx(ReferenceWrapper, { ...interactions.getReferenceProps(), ref: floating.refs.setReference, "$inline": inline, children: trigger })), _open && (jsx(FloatingPortal, { root: root, children: jsx(FloatingFocusManager, { context: floating.context, modal: false, children: jsx("div", { ref: floating.refs.setFloating, style: floating.floatingStyles, ...interactions.getFloatingProps(), children: typeof children === "function"
                            ? children({ closeMenu })
                            : children }) }) }))] }));
}
const ReferenceWrapper = styled.div `
  display: ${({ $inline }) => ($inline ? "inline-block" : "block")};
`;

export { Popover };
